import React, { useState, useEffect } from "react";
import { Col, Pagination, Row } from "react-bootstrap";
import { ArrowUpDown, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { CiSearch } from "react-icons/ci";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { LuDownload } from "react-icons/lu";
// import Chart from "../Charts/Chart";
import Dropdown from "react-bootstrap/Dropdown";
import {
    GripVertical, ChevronLeft,
    ChevronRight, Settings
} from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";

const FASparentMultipalChild = ({
    columns = [],
    data = [],
    apiTotals = null,
    externalTotals = {},
    groupTotals = {},
    initialGroupLevels = [],
    onConfigureBuckets
}) => {
    const C = {
        bg: "#ffffff",
        bgAlt: "#f8fafc",
        surface: "#ffffff",
        border: "#e2e8f0",
        accent: "#3b82f6",
        accentText: "#ffffff",
        text: "#0f172a",
        textMuted: "#64748b",
        btnBg: "#f8fafc",
        btnText: "#0f172a",
        theadBg: "#f1f5f9",
        theadText: "#0f172a",
        totalBg: "#f1f5f9",
        totalText: "#0f172a",
        footerBg: "#f8fafc",
        footerText: "#0f172a",
        pageBg: "#f8fafc",
        pageActive: "#3b82f6",
        pageText: "#64748b",
        inputBg: "#ffffff",
        inputText: "#0f172a",
        inputBorder: "#cbd5e1",
        placeholder: "#94a3b8",
        groupL0: "#f8fafc",
        groupL1: "#f1f5f9",
        groupL2: "#e2e8f0",
        subTotal: "#f1f5f9",
    };

    /* ─── STATE ─── */
    const [groupLevels, setGroupLevels] = useState(initialGroupLevels);
    const [visibleColumns, setVisibleColumns] = useState(columns.map(c => c.key));
    const [prevColumnsStr, setPrevColumnsStr] = useState(columns.map(c => c.key).join(','));

    useEffect(() => {
        const currentKeys = columns.map(c => c.key).join(',');
        if (prevColumnsStr !== currentKeys) {
            setVisibleColumns(columns.map(c => c.key));
            setPrevColumnsStr(currentKeys);
        }
    }, [columns, prevColumnsStr]);

    useEffect(() => {
        setGroupLevels(initialGroupLevels);
    }, [initialGroupLevels.join(',')]);

    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [hoveredRow, setHoveredRow] = useState(null);
    const [isAllExpanded, setIsAllExpanded] = useState(true);

    const rowsPerPage = 25;

    const labelMap = columns.reduce((acc, c) => { acc[c.key] = c.label; return acc; }, {});

    const activeCols = columns.filter(c => visibleColumns.includes(c.key));

    /* ─── COLLECT ALL GROUP KEYS (for expand/collapse all) ─── */
    const getAllGroupKeys = (data, parentKey = "", level = 0) => {
        const keys = [];
        (data || []).forEach((item) => {
            const gk = `${parentKey}-${item.groupName}-${level}`;
            keys.push(gk);
            if (Array.isArray(item.children) && item.children[0]?.children) {
                keys.push(...getAllGroupKeys(item.children, gk, level + 1));
            }
        });
        return keys;
    };

    const handleExpandCollapseAll = () => {
        if (isAllExpanded) {
            // Collapse all – fill collapsedGroups with every group key
            const allKeys = getAllGroupKeys(hierarchyData);
            setCollapsedGroups(new Set(allKeys));
            setIsAllExpanded(false);
        } else {
            // Expand all – clear collapsedGroups
            setCollapsedGroups(new Set());
            setIsAllExpanded(true);
        }
    };

    /* ─── SEARCH ─── */
    const filteredData = (data ?? []).filter((row) =>
        Object.values(row).some((v) =>
            v?.toString().toLowerCase().includes(search.trim().toLowerCase())
        )
    );

    /* ─── SORT ─── */
    const handleSort = (key) => {
        if (sortConfig.key === key) {
            if (sortConfig.direction === "desc") { setSortConfig({ key, direction: "asc" }); return; }
            if (sortConfig.direction === "asc") { setSortConfig({ key: null, direction: null }); return; }
        }
        setSortConfig({ key, direction: "desc" });
    };

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let av = a[sortConfig.key], bv = b[sortConfig.key];
        const ca = String(av).replace(/,/g, ""), cb = String(bv).replace(/,/g, "");
        if (!isNaN(ca) && !isNaN(cb)) { av = Number(ca); bv = Number(cb); }
        if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
        if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    /* ─── PAGINATION ─── */
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);

    const visiblePages = 4;
    let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let endPage = Math.min(totalPages, startPage + visiblePages - 1);
    if (endPage - startPage + 1 < visiblePages) startPage = Math.max(1, endPage - visiblePages + 1);

    /* ─── FORMAT ─── */
    const fmtIN = (n) => {
        const num = Number(n);
        return isNaN(num) ? n : num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const fmtRs = (n) => {
        if (n == null || n === "") return "0";
        const num = parseFloat(n);
        return isNaN(num) ? "0" : num.toLocaleString("en-IN");
    };
    const dashColumns = ["AcName", "CollectionName", "GroupName", "GroupMstName", "ProductionGroupName"];
    const isNumeric = (colDef, rows) =>
        rows.some((r) => {
            const v = r[colDef.key];
            return v != null && v !== "" && !isNaN(String(v).replace(/,/g, ""));
        });



    /* ─── CSV ─── */
    const downloadCSV = () => {
        if (!sortedData.length) return;
        const headers = activeCols.map((c) => c.label);
        const rows = sortedData.map((row) =>
            activeCols.map((c) => {
                let v = row[c.key];
                if (typeof v === "string" && v.includes(",")) v = '"' + v + '"';
                return v ?? "";
            })
        );
        const blob = new Blob([[headers, ...rows].map((r) => r.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.setAttribute("download", "table_data.csv");
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    /* ─── HIERARCHY HELPERS ─── */
    const getLeafCount = (items) =>
        items.reduce((n, item) => n + (item?.children ? getLeafCount(item.children) : 1), 0);

    const buildHierarchy = (rows, levels, levelIndex = 0) => {
        if (levelIndex >= levels.length) return rows || [];
        const key = levels[levelIndex];
        const grouped = {};
        rows.forEach((r) => { const v = r[key] || "-"; (grouped[v] = grouped[v] || []).push(r); });
        return Object.entries(grouped).map(([groupName, children]) => {
            const totals = {};
            columns.forEach((c) => {
                let t = 0;
                children.forEach((r) => { const v = String(r[c.key] || 0).replace(/,/g, ""); if (!isNaN(v)) t += Number(v); });
                totals[c.key] = t;
            });
            const subChildren = buildHierarchy(children, levels, levelIndex + 1);
            return { groupName, level: levelIndex, totals, rowCount: getLeafCount(subChildren), children: subChildren };
        });
    };

    const grandTotals = {};
    columns.forEach((c) => {
        let t = 0;
        sortedData.forEach((r) => { const v = String(r[c.key] || 0).replace(/,/g, ""); if (!isNaN(v)) t += Number(v); });
        grandTotals[c.key] = t;
    });

    const hierarchyData = groupLevels.length > 0 ? buildHierarchy(sortedData, groupLevels) : [];

    const toggleGroup = (gk) =>
        setCollapsedGroups((prev) => {
            const s = new Set(prev);
            s.has(gk) ? s.delete(gk) : s.add(gk);
            return s;
        });

    const groupLevelBg = (level) => [C.groupL0, C.groupL1, C.groupL2][Math.min(level, 2)];

    const getGroupTotal = (groupKey, columnKey, localTotal) => {
        if (
            groupTotals &&
            groupTotals[groupKey] &&
            groupTotals[groupKey][columnKey] !== undefined
        ) {
            return groupTotals[groupKey][columnKey];
        }

        return localTotal;
    };

    /* ─── SORTABLE ITEM (Group-by panel) ─── */
    const SortableItem = ({ id, label, checked, toggleItem }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
        return (
            <div
                ref={setNodeRef}
                style={{
                    transform: CSS.Transform.toString(transform), transition,
                    touchAction: "none", cursor: "pointer",
                    padding: "9px 12px", borderRadius: "8px", marginBottom: "8px",
                    display: "flex", alignItems: "center", gap: "10px", fontWeight: "600",
                    background: checked ? C.accent + '18' : C.inputBg,
                    border: '1.5px solid ' + (checked ? C.accent : C.border),
                    color: C.text,
                }}
                onClick={toggleItem}
            >
                <div {...attributes} {...listeners} style={{ color: C.textMuted, cursor: "grab" }}>
                    <GripVertical size={15} />
                </div>
                <div style={{
                    width: 17, height: 17, borderRadius: "50%", flexShrink: 0,
                    background: checked ? C.accent : "transparent",
                    border: `1.5px solid ${checked ? C.accent : C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: "10px",
                }}>
                    {checked && "✓"}
                </div>
                <span style={{ fontSize: 13 }}>{label}</span>
            </div>
        );
    };

    /* ─── HIERARCHY RENDERER ─── */
    const RenderHierarchy = ({ data, columns, level = 0, parentKey = "" }) => {
        let prevRow = {};
        return (
            <>
                {data.map((item, idx) => {
                    const gk = `${parentKey}-${item.groupName}-${level}`;
                    const isCollapsed = collapsedGroups.has(gk);
                    const isLastLevel = Array.isArray(item.children) && item.children.length > 0 && !item.children[0]?.children;
                    const shortLabelMap = {
                        NetBalCrAmt: "NetBal CR",
                        NetBalDrAmt: "NetBal DR",

                    };
                    return (
                        <React.Fragment key={idx}>
                            {/* ── GROUP HEADER ROW ── */}
                            <tr style={{ background: groupLevelBg(level), borderBottom: `1px solid ${C.border}` }}>
                                {columns.map((c, ci) => {
                                    if (ci === 0) {
                                        return (
                                            <td
                                                key={ci}
                                                style={{ paddingLeft: `${level * 24 + 16}px`, paddingTop: 9, paddingBottom: 9, paddingRight: 10, cursor: "pointer" }}
                                                onClick={() => toggleGroup(gk)}
                                            >
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <div style={{
                                                        width: 20, height: 20, borderRadius: 4, background: C.accent,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        color: C.accentText, fontSize: 13, fontWeight: 900, flexShrink: 0,
                                                    }}>
                                                        {isCollapsed ? "+" : "-"}
                                                    </div>
                                                    <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>
                                                        {labelMap[groupLevels[level]]}: {item.groupName}
                                                    </span>
                                                </div>
                                            </td>
                                        );
                                    }

                                    let tVal = "";
                                    if (!["srNumber", "id"].includes(c.key) && !dashColumns.includes(c.key) && isNumeric(c, sortedData)) {
                                        const t = getGroupTotal(gk, c.key, item.totals[c.key]);
                                        if (t != null && !isNaN(t)) {
                                            tVal = c.rupees ? fmtRs(t) : c.format ? fmtIN(t) : t;
                                        }
                                    }

                                    return (
                                        <td key={ci} style={{ padding: "9px 12px", textAlign: c.align || "left", fontSize: 13, color: C.text, fontWeight: 700 }}>
                                            {tVal}
                                        </td>
                                    );
                                })}
                            </tr>

                            {!isCollapsed && (
                                <>
                                    {isLastLevel
                                        ? (item.children || []).map((row, ri) => {
                                            const trStyle = {
                                                background: ri % 2 === 0 ? C.bg : C.bgAlt,
                                                transition: "background 0.12s",
                                            };
                                            return (
                                                <tr key={ri} style={trStyle}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = `${C.accent}18`; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = ri % 2 === 0 ? C.bg : C.bgAlt; }}
                                                >
                                                    {columns.map((c, ci) => {
                                                        let val = row[c.key];
                                                        const gi = groupLevels.indexOf(c.key);
                                                        if (gi !== -1) {
                                                            let hide = true;
                                                            for (let i = 0; i <= gi; i++) {
                                                                if (prevRow[groupLevels[i]] !== row[groupLevels[i]]) { hide = false; break; }
                                                            }
                                                            if (hide) val = "";
                                                        }
                                                        return (
                                                            <td key={ci} style={{
                                                                textAlign: c.align || "left", padding: "10px 12px",
                                                                fontSize: 13, color: C.text, verticalAlign: "middle",
                                                                borderBottom: `1px solid ${C.border}`,
                                                            }}>
                                                                {c.rupees ? fmtRs(val) : c.format ? fmtIN(val) : val}
                                                            </td>
                                                        );
                                                    })}
                                                    {(() => { prevRow = row; return null; })()}
                                                </tr>
                                            );
                                        })
                                        : <RenderHierarchy data={item.children} columns={columns} level={level + 1} parentKey={gk} />
                                    }

                                    {/* SUB-TOTAL */}
                                    {item.rowCount > 1 && (
                                        <tr style={{ background: C.subTotal, borderTop: `2px solid ${C.accent}`, fontSize: 14 }}>
                                            {columns.map((c, ci) => {
                                                if (ci === 0) return (
                                                    <td key={ci} style={{ paddingLeft: `${(level + 1) * 32}px`, fontWeight: 700, color: C.accent, padding: "9px 12px", }}>
                                                        {labelMap[groupLevels[level]]} Total
                                                    </td>
                                                );
                                                const t = getGroupTotal(
                                                    gk,
                                                    c.key,
                                                    item.totals[c.key]
                                                );
                                                if (dashColumns.includes(c.key))
                                                    return <td key={ci} style={{ fontWeight: 700, textAlign: c.align || "left", color: C.text, padding: "9px 12px" }}>—</td>;
                                                return (
                                                    <td key={ci} style={{ fontWeight: 700, textAlign: c.align || "left", color: C.text, padding: "9px 12px", }}>
                                                        {isNumeric(c, sortedData) && t != null && !isNaN(t)
                                                            ? (c.rupees ? fmtRs(t) : c.format ? fmtIN(t) : t)
                                                            : ""}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    )}
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    /* ─── DRAG END ─── */
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oi = groupLevels.indexOf(active.id);
        const ni = groupLevels.indexOf(over.id);
        setGroupLevels((items) => arrayMove(items, oi, ni));
    };

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    return (
        <>
            {/* ─── global scoped styles ─── */}
            <style>{`
                .fas-pmc-wrap *::-webkit-scrollbar { width: 8px; height: 12px; }
                .fas-pmc-wrap *::-webkit-scrollbar-track { background: ${C.surface}; }
                .fas-pmc-wrap *::-webkit-scrollbar-thumb { background: ${C.accent}; border-radius: 10px; }
                /* ── Remove ALL vertical column divider lines ── */
                .fas-pmc-wrap table td,
                .fas-pmc-wrap table th {
                    border-right: none !important;
                    border-left: none !important;
                    white-space: nowrap !important;
                }
                .fas-pmc-pg .page-item .page-link {
                    border: none; border-radius: 7px; min-width: 33px; height: 33px;
                    display: flex; align-items: center; justify-content: center;
                    background: ${C.pageBg}; color: ${C.pageText}; font-weight: 600; font-size: 13px; margin: 0 2px;
                    transition: background 0.15s, color 0.15s;
                }
                .fas-pmc-pg .page-item.active .page-link {
                    background: ${C.pageActive} !important; color: #fff !important;
                    box-shadow: 0 2px 10px ${C.pageActive}55;
                }
                .fas-pmc-pg .page-item.disabled .page-link { opacity: 0.4; }
                .fas-pmc-pg .page-item .page-link:hover:not([aria-current]) { background: ${C.accent}33; color: ${C.accent}; }
                .fas-pmc-search:focus { outline: none; border-color: ${C.accent} !important; box-shadow: 0 0 0 3px ${C.accent}22 !important; }
                .fas-pmc-dl:hover { opacity: 0.85; transform: scale(1.02); }

                 .custom-pagination {
    gap: 8px;
  }

  /* DEFAULT */
  .custom-pagination .page-item .page-link {
    border: none !important;
    border-radius: 8px !important;
    min-width: 34px;
    height: 34px;
    display: flex !important;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    background: #f8fafc;
    color: #64748b;
  }

  /* ACTIVE */
  .custom-pagination .page-item.active .page-link {
    background: #3b82f6 !important;
    color: #ffffff !important;
  }

  /* HOVER */
  .custom-pagination .page-item .page-link:hover {
    background: #f1f5f9;
  }


    .search-input::placeholder {
      color: #94a3b8;
      opacity: 1;
    }

    @media (max-width: 991px) {
      .search-input {
        font-size: 13px !important;
      }
    }

    @media (max-width: 768px) {
      .search-wrapper {
        flex: 1 !important;
        width: auto !important;
        min-width: 0 !important;
      }

      .download-btn {
        width: 40px;
        height: 40px;
        padding: 8px;
      }

      .download-text {
        display: none;
      }
    }
    .hover-scale-btn {
            transition: transform 0.2s ease-in-out;
          }
          .hover-scale-btn:hover {
            transform: scale(1.02);
          }
    @media (max-width: 576px) {
      .top-bar-wrap {
        gap: 8px !important;
      }
    }
            `}</style>





            <div className="fas-pmc-wrap" style={{ background: C.surface, borderRadius: "0 0 12px 12px" }}>

                {/* ══ TOP BAR ══ */}
                <Row
                    className="g-3 align-items-center justify-content-between"
                    style={{
                        padding: "12px 14px",
                        borderBottom: `1px solid ${C.border}`,
                    }}
                >
                    {/* ───────── LEFT SIDE ───────── */}
                    <Col xl={5} lg={12} md={12} sm={12} xs={12}>
                        {/* Search */}
                        <div style={{ position: "relative", width: "100%" }}>
                            <CiSearch
                                size={18}
                                style={{
                                    position: "absolute",
                                    left: 11,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: C.textMuted,
                                    pointerEvents: "none",
                                }}
                            />

                            <input
                                type="text"
                                placeholder="Search..."
                                className="fas-pmc-search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    paddingLeft: 36,
                                    height: 40,
                                    width: "100%",
                                    fontSize: 14,
                                    border: `1.5px solid ${C.border}`,
                                    borderRadius: 10,
                                    background: C.inputBg,
                                    color: C.inputText,
                                    transition: "border 0.15s",
                                }}
                            />
                        </div>
                    </Col>

                    {/* ───────── RIGHT SIDE ───────── */}
                    <Col xl={7} lg={12} md={12} sm={12} xs={12}>
                        <Row className="g-2 justify-content-xl-end justify-content-center">

                            {/* GROUP BY */}
                            <Col xs={12} sm="auto">
                                <div
                                    style={{
                                        position: "relative",
                                        zIndex: 99999,
                                    }}
                                >
                                    <Dropdown autoClose="outside" drop="down">
                                        <Dropdown.Toggle
                                            id="fas-group-toggle"
                                            className="w-100"
                                            style={{
                                                minHeight: 42,
                                                width: "100%",
                                                padding: "10px 14px",
                                                fontSize: 13,
                                                fontWeight: 700,
                                                background: C.inputBg,
                                                color: C.accent,
                                                border: `1.5px solid ${C.accent}`,
                                                borderRadius: 9,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                                boxShadow: "none",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            <span style={{ fontSize: 15 }}>☰</span>

                                            <span
                                                style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                }}
                                            >
                                                Group By ({groupLevels.length})
                                            </span>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu
                                            align="end"
                                            renderOnMount
                                            popperConfig={{
                                                strategy: "fixed",
                                            }}
                                            style={{
                                                width: "100%",
                                                minWidth: 280,
                                                maxWidth: 340,
                                                padding: 0,
                                                borderRadius: 14,
                                                border: `1px solid ${C.border}`,
                                                overflow: "hidden",
                                                boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                                                background: C.inputBg,
                                                zIndex: 9999,
                                                position: "absolute",
                                            }}
                                        >
                                            {/* Header */}
                                            <div
                                                style={{
                                                    padding: "12px 14px",
                                                    background: C.accent,
                                                }}
                                            >
                                                <Row className="align-items-center gy-2">
                                                    <Col xs={12} sm={6}>
                                                        <span
                                                            style={{
                                                                fontWeight: 700,
                                                                fontSize: 14,
                                                                color: "#fff",
                                                            }}
                                                        >
                                                            Reorder & Group By
                                                        </span>
                                                    </Col>

                                                    <Col xs={12} sm={6}>
                                                        <div
                                                            className="d-flex justify-content-sm-end gap-3 flex-wrap"
                                                            style={{
                                                                fontSize: 12,
                                                                color: "#fff",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    cursor: "pointer",
                                                                    textDecoration: "underline",
                                                                }}
                                                                onClick={() =>
                                                                    setGroupLevels(columns.filter(c => !isNumeric(c, sortedData)).map((c) => c.key))
                                                                }
                                                            >
                                                                Select All
                                                            </span>

                                                            <span
                                                                style={{
                                                                    cursor: "pointer",
                                                                    textDecoration: "underline",
                                                                }}
                                                                onClick={() => setGroupLevels([])}
                                                            >
                                                                Clear
                                                            </span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {/* Body */}
                                            <div
                                                style={{
                                                    padding: 10,
                                                    maxHeight: 360,
                                                    overflowY: "auto",
                                                }}
                                            >
                                                <DndContext
                                                    collisionDetection={closestCenter}
                                                    onDragEnd={handleDragEnd}
                                                    modifiers={[
                                                        restrictToVerticalAxis,
                                                        restrictToParentElement,
                                                    ]}
                                                >
                                                    <SortableContext
                                                        items={groupLevels}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        {groupLevels.map((item) => {
                                                            const column = columns.find(
                                                                (c) => c.key === item
                                                            );

                                                            if (!column) return null;

                                                            return (
                                                                <SortableItem
                                                                    key={item}
                                                                    id={item}
                                                                    label={column.label}
                                                                    checked={true}
                                                                    toggleItem={() =>
                                                                        setGroupLevels(
                                                                            groupLevels.filter(
                                                                                (g) => g !== item
                                                                            )
                                                                        )
                                                                    }
                                                                />
                                                            );
                                                        })}

                                                        {columns
                                                            .filter(
                                                                (c) => !groupLevels.includes(c.key) && !isNumeric(c, sortedData)
                                                            )
                                                            .map((col) => (
                                                                <div
                                                                    key={col.key}
                                                                    onClick={() =>
                                                                        setGroupLevels([
                                                                            ...groupLevels,
                                                                            col.key,
                                                                        ])
                                                                    }
                                                                    style={{
                                                                        padding: "10px 12px",
                                                                        borderRadius: 10,
                                                                        marginBottom: 8,
                                                                        background: C.inputBg,
                                                                        border: `1px solid ${C.border}`,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 10,
                                                                        cursor: "pointer",
                                                                        fontWeight: 600,
                                                                        color: C.text,
                                                                        fontSize: 13,
                                                                        width: "100%",
                                                                        overflow: "hidden",
                                                                    }}
                                                                >
                                                                    <GripVertical
                                                                        size={15}
                                                                        style={{
                                                                            color: C.textMuted,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />

                                                                    <div
                                                                        style={{
                                                                            minWidth: 17,
                                                                            width: 17,
                                                                            height: 17,
                                                                            borderRadius: "50%",
                                                                            border: `1.5px solid ${C.border}`,
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />

                                                                    <span
                                                                        style={{
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {col.label}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                    </SortableContext>
                                                </DndContext>
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </Col>
                            {/* COLUMNS VISIBILITY */}
                            <Col xs={12} sm="auto">
                                <div style={{ position: "relative", zIndex: 99999 }}>
                                    <Dropdown autoClose="outside" drop="down">
                                        <Dropdown.Toggle
                                            id="fas-columns-toggle"
                                            className="w-100"
                                            style={{
                                                minHeight: 40,
                                                padding: "0 14px",
                                                fontSize: 13,
                                                fontWeight: 700,
                                                background: C.inputBg,
                                                color: C.accent,
                                                border: `1.5px solid ${C.accent}`,
                                                borderRadius: 9,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 8,
                                                boxShadow: "none",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Settings size={16} /> Columns
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto', padding: '10px' }}>
                                            {columns.map(c => (
                                                <div key={c.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}
                                                    onClick={() => {
                                                        if (visibleColumns.includes(c.key)) {
                                                            setVisibleColumns(prev => prev.filter(k => k !== c.key));
                                                        } else {
                                                            setVisibleColumns(prev => [...prev, c.key]);
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns.includes(c.key)}
                                                        readOnly
                                                        style={{ marginRight: '8px', cursor: 'pointer' }}
                                                    />
                                                    <span style={{ fontSize: 13, color: C.text }}>{c.label}</span>
                                                </div>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </Col>

                            {/* Expand Collapse */}
                            <Col xs={12} sm="auto">
                                <button
                                    onClick={
                                        groupLevels.length > 0
                                            ? handleExpandCollapseAll
                                            : undefined
                                    }
                                    disabled={groupLevels.length === 0}
                                    style={{
                                        height: 40,
                                        width: "100%",
                                        padding: "0 16px",
                                        background:
                                            groupLevels.length === 0
                                                ? C.border
                                                : isAllExpanded
                                                    ? C.inputBg
                                                    : C.accent,
                                        color:
                                            groupLevels.length === 0
                                                ? C.textMuted
                                                : isAllExpanded
                                                    ? C.accent
                                                    : "#fff",
                                        border: `1.5px solid ${groupLevels.length === 0
                                            ? C.border
                                            : C.accent
                                            }`,
                                        borderRadius: 10,
                                        fontWeight: 700,
                                        fontSize: 13,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 6,
                                        cursor:
                                            groupLevels.length === 0
                                                ? "not-allowed"
                                                : "pointer",
                                        opacity:
                                            groupLevels.length === 0 ? 0.55 : 1,
                                        transition:
                                            "background 0.2s, color 0.2s",
                                    }}
                                >
                                    <span style={{ fontSize: 16 }}>
                                        {isAllExpanded ? "⊟" : "⊞"}
                                    </span>

                                    {isAllExpanded
                                        ? "Collapse All"
                                        : "Expand All"}
                                </button>
                            </Col>

                            {/* Configure Buckets */}
                            {onConfigureBuckets && (
                                <Col xs={12} sm="auto">
                                    <button
                                        onClick={onConfigureBuckets}
                                        style={{
                                            height: 40,
                                            width: "100%",
                                            padding: "0 18px",
                                            background: C.inputBg,
                                            color: C.accent,
                                            border: `1.5px solid ${C.accent}`,
                                            borderRadius: 10,
                                            fontWeight: 700,
                                            fontSize: 14,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            cursor: "pointer",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <Settings size={16} />
                                        <span>Configure Buckets</span>
                                    </button>
                                </Col>
                            )}

                            {/* Export Excel */}
                            <Col xs={12} sm="auto">
                                <button
                                    onClick={downloadCSV}
                                    className="fas-pmc-dl"
                                    style={{
                                        height: 40,
                                        width: "100%",
                                        padding: "0 18px",
                                        background: C.btnBg,
                                        color: C.btnText,
                                        border: "none",
                                        borderRadius: 10,
                                        fontWeight: 700,
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 6,
                                        cursor: "pointer",
                                        transition:
                                            "opacity 0.15s, transform 0.15s",
                                    }}
                                >
                                    <LuDownload size={16} />
                                    <span>Export Excel</span>
                                </button>
                            </Col>
                        </Row>
                    </Col>
                </Row>


                {/* ══ TABLE ══ */}
                <div
                    style={{
                        overflowX: "auto",
                        overflowY: "visible",
                        maxHeight: 600,
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700, tableLayout: "auto", }}>

                        {/* THEAD */}
                        <thead>
                            <tr>
                                {activeCols.map((c, i) => (
                                    <th key={i}
                                        style={{
                                            background: C.theadBg, color: C.theadText, padding: "10px 12px",
                                            fontWeight: 700, fontSize: 13, textAlign: c.align || "left",
                                            position: "sticky", top: 0, zIndex: 10, cursor: "pointer",
                                            borderBottom: `2px solid ${C.accent}`
                                        }}
                                        onClick={() => handleSort(c.key)}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: c.align === "right" ? "flex-end" : "flex-start" }}>
                                            {c.label}
                                            {sortConfig.key === c.key ? (
                                                sortConfig.direction === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                            ) : (
                                                <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* TBODY */}
                        <tbody>
                            {hierarchyData.length > 0 ? (
                                <RenderHierarchy data={hierarchyData} columns={activeCols} />
                            ) : currentRows.length > 0 ? (
                                currentRows.map((row, ri) => (
                                    <tr key={ri} style={{ background: ri % 2 === 0 ? C.bg : C.bgAlt, transition: "background 0.12s" }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = `${C.accent}18`; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = ri % 2 === 0 ? C.bg : C.bgAlt; }}
                                    >
                                        {activeCols.map((c, ci) => (
                                            <td key={ci} style={{ textAlign: c.align || "left", padding: "10px 12px", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}` }}>
                                                {c.rupees ? fmtRs(row[c.key]) : c.format ? fmtIN(row[c.key]) : row[c.key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={activeCols.length} style={{ textAlign: "center", padding: 30, color: C.textMuted }}>
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>

                        {/* TFOOTER FOR GRAND TOTAL */}
                        {sortedData.length > 0 && activeCols.some(c => isNumeric(c, sortedData)) && (
                            <tfoot style={{ position: "sticky", bottom: 0, zIndex: 9, background: C.totalBg, color: C.totalText }}>
                                <tr>
                                    {activeCols.map((c, ci) => {
                                        if (ci === 0) return <td key={ci} style={{ padding: "12px 14px", fontWeight: 800, fontSize: 14 }}>Grand Total</td>;
                                        if (dashColumns.includes(c.key)) return <td key={ci} style={{ padding: "12px 14px", fontWeight: 800, textAlign: c.align || "left" }}>—</td>;
                                        const gt = grandTotals[c.key];
                                        return (
                                            <td key={ci} style={{ padding: "12px 14px", fontWeight: 800, fontSize: 14, textAlign: c.align || "left" }}>
                                                {isNumeric(c, sortedData) && gt != null && !isNaN(gt) ? (c.rupees ? fmtRs(gt) : c.format ? fmtIN(gt) : gt) : ""}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {/* ══ PAGINATION ══ */}
                {hierarchyData.length === 0 && totalPages > 1 && (
                    <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                        <span style={{ fontSize: 13, color: C.textMuted, fontWeight: 600 }}>
                            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, sortedData.length)} of {sortedData.length} Entries
                        </span>
                        <Pagination className="mb-0 custom-pagination">
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                            {startPage > 1 && (
                                <>
                                    <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
                                    {startPage > 2 && <Pagination.Ellipsis disabled />}
                                </>
                            )}
                            {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(p => (
                                <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>
                                    {p}
                                </Pagination.Item>
                            ))}
                            {endPage < totalPages && (
                                <>
                                    {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
                                    <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                                </>
                            )}
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.max(1, p + 1))} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </div>
        </>
    );
};

export default FASparentMultipalChild;
